import classNames from "classnames";
import map from "lodash/map";
import React, { FunctionComponent, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { v1 as uuid } from "uuid";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconFacebook } from "../../../../icons/facebook.svg";
import { ReactComponent as GoogleIcon } from "../../../../icons/google.svg";
import { ReactComponent as IconInstagram } from "../../../../icons/instagram.svg";
import { ReactComponent as IconPictures } from "../../../../icons/pictures.svg";
import { ReactComponent as IconX } from "../../../../icons/x.svg";
import BlankSelectionProvider from "../../../../providers/selection/BlankSelectionProvider";
import FacebookSelectionProvider from "../../../../providers/selection/FacebookSelectionProvider";
import GoogleSelectionProvider from "../../../../providers/selection/GoogleSelectionProvider";
import InstagramSelectionProvider from "../../../../providers/selection/InstagramSelectionProvider";
import UnsplashSelectionProvider from "../../../../providers/selection/UnsplashSelectionProvider";
import { Panel } from "../../../../states/customization";
import { SelectionProviderToken } from "../../../../types/customization/configuration";
import { ContentPage } from "../../../../types/customization/content";
import { SelectionPhoto, SelectionProvider } from "../../../../types/selection/selection";
import {
  countPages,
  hasSelectionInPanel,
  maxPageReached,
} from "../../../../utils/customization/configuration";
import {
  countMinMaxTotalEditablePicturesInContent,
  countMinMaxTotalEmptyEditablePicturesInContent,
} from "../../../../utils/customization/content";
import {
  getCoverFilteredPages,
  getNoCoverDefinitions,
} from "../../../../utils/customization/cover";
import {
  findPage,
  findPageDefinition,
  findPageDefinitionsWithValidCondition,
  findPagesWithEmptyEditablePictures,
} from "../../../../utils/customization/finders";
import { findSelectionPhotosInPages } from "../../../../utils/customization/page";
import {
  countEditablePicturesInPage,
  findMinMaxEditablePictures,
  findMinMaxEditablePicturesInPage,
  findPageDefinitionsForAdditionalPages,
  getNextLayoutWithMorePictures,
} from "../../../../utils/customization/template";
import {
  findPhoto,
  isPhotoInPicturesList,
  selectionProviderTokenFor,
} from "../../../../utils/selection/finders";
import { orderSelectionByDate } from "../../../../utils/selection/list";
import { handleFile } from "../../../../utils/selection/upload";
import { getVariableByEnv } from "../../../../utils/variableByEnv";
import Button from "../../../atoms/Button";
import ConfirmModal from "../../../molecules/ConfirmModal";
import SelectionHeaderProvider from "../../../molecules/selection/SelectionHeaderProvider";
import SelectionUploadSummary from "../../../molecules/selection/SelectionUploadSummary";
import SelectionProviderBrowser from "../SelectionProviderBrowser";
import UploadManager from "../UploadManager";
import "./style.scss";

const initialProvider = getVariableByEnv("NODE_ENV") === "development" ? "unsplash" : "upload";

interface Props {
  fixedLayout?: boolean;
  pageKey?: string;
  undoKey?: string;
}

const SelectionModal: FunctionComponent<Props> = ({ fixedLayout = false, pageKey, undoKey }) => {
  const intl = useIntl();
  const [galleryKey] = useState<string>(undoKey || uuid());

  const {
    configuration,
    content,
    dispatch,
    selection: { photos: selectionPhotos },
    template,
    ui: { editedPicture, panel },
  } = useCustomizationContext();
  const [currentProvider, setCurrentProvider] = useState<SelectionProvider>(initialProvider);
  const [selectedPictures, setSelectedPictures] = useState<SelectionPhoto[]>([]);
  const [removedPictures, setRemovedPictures] = useState<SelectionPhoto[]>([]);
  const [uploadingQuantity, setUploadingQuantity] = useState<number | undefined>();
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [actionToConfirm, setActionToConfirm] = useState<"close" | "valid">();
  const [agreeEarlyClosing, setAgreeEarlyClosing] = useState<boolean>(false);

  const minPages = configuration && configuration.pages && configuration.pages.min;
  const maxPages = configuration && configuration.pages && configuration.pages.max;
  const conditionFilteredPageDefinitions =
    findPageDefinitionsWithValidCondition(template, content) || [];
  const noCoverPageDefinitions = getNoCoverDefinitions(conditionFilteredPageDefinitions);
  const pageDefinitionsToAddNext = findPageDefinitionsForAdditionalPages(
    conditionFilteredPageDefinitions,
    template.contentPageConfiguration
  );
  const pagesWithoutCover = useMemo(
    () => getCoverFilteredPages(content.pages, noCoverPageDefinitions),
    [content.pages, noCoverPageDefinitions]
  );

  const handleChangeCurrentProvider = (provider: SelectionProvider) => {
    setCurrentProvider(provider);
  };

  const selectionProviderConnect = configuration
    ? configuration.selectionProviderConnect
    : undefined;
  const selectionProviderDisconnect = configuration
    ? configuration.selectionProviderDisconnect
    : undefined;
  const uploadProvider = configuration ? configuration.uploadProvider : undefined;

  const selectionProviderTokens: SelectionProviderToken[] = useMemo(() => {
    if (!configuration || !configuration.selectionProviderTokens) {
      return [];
    }
    return configuration.selectionProviderTokens;
  }, [configuration]);

  const facebookSelectionProvider = useMemo(() => {
    const token = selectionProviderTokenFor("facebook", selectionProviderTokens);
    return token ? new FacebookSelectionProvider(token) : new BlankSelectionProvider();
  }, [selectionProviderTokens]);
  const instagramSelectionProvider = useMemo(() => {
    const token = selectionProviderTokenFor("instagram", selectionProviderTokens);
    return token ? new InstagramSelectionProvider(token) : new BlankSelectionProvider();
  }, [selectionProviderTokens]);
  const googleSelectionProvider = useMemo(() => {
    const token = selectionProviderTokenFor("google_photos", selectionProviderTokens);
    return token ? new GoogleSelectionProvider(token) : new BlankSelectionProvider();
  }, [selectionProviderTokens]);
  const unsplashSelectionProvider = useMemo(() => {
    return new UnsplashSelectionProvider();
  }, []);
  const uploadSelectionProvider = useMemo(() => {
    return configuration?.selectionUploadProvider
      ? configuration.selectionUploadProvider
      : new BlankSelectionProvider();
  }, [configuration]);

  const nbMaxPages = maxPages ? maxPages : 0;
  let currentEmptyEditablePictures = 0;
  let minEditablePictures = 0;
  let minEmptyEditablePictures = 0;
  let maxEditablePictures = 0;
  let maxEmptyEditablePictures = 0;
  let page: ContentPage | null = null;

  if (editedPicture) {
    // min & max for image opened in picture editor
    currentEmptyEditablePictures = 1;
    maxEmptyEditablePictures = maxEditablePictures = 1;
    minEmptyEditablePictures = minEditablePictures = 1;
    page = findPage(editedPicture.pageKey, content);
  } else if (pageKey) {
    // min & max for selected page
    page = findPage(pageKey, content);
    const pageDefinition = findPageDefinition(page.pageDefinitionKey, template);
    const picturePlacesInPage = countEditablePicturesInPage(pageDefinition);
    currentEmptyEditablePictures = picturePlacesInPage - page.editablePictures.length;
    minEditablePictures = 1;
    minEmptyEditablePictures = currentEmptyEditablePictures;
    if (currentEmptyEditablePictures === 0) {
      const nearestTemplate = getNextLayoutWithMorePictures(
        page.pageDefinitionKey,
        conditionFilteredPageDefinitions
      );
      if (nearestTemplate) {
        minEmptyEditablePictures =
          countEditablePicturesInPage(nearestTemplate) - page.editablePictures.length;
      }
    }
    const minMax = findMinMaxEditablePicturesInPage(
      page.pageDefinitionKey,
      conditionFilteredPageDefinitions
    );
    maxEditablePictures = fixedLayout ? picturePlacesInPage : minMax.max;
    maxEmptyEditablePictures = fixedLayout
      ? currentEmptyEditablePictures
      : minMax.max - page.editablePictures.length;
  } else {
    // min & max for all pages (except cover for albums)
    const pagesCount = countPages(content, noCoverPageDefinitions);
    const totalMinMaxInContent = countMinMaxTotalEditablePicturesInContent(
      template,
      pagesWithoutCover
    );
    const totalMinMaxSlotsInContent = countMinMaxTotalEmptyEditablePicturesInContent(
      template,
      pagesWithoutCover
    );
    const minMax = findMinMaxEditablePictures(noCoverPageDefinitions);
    const minMaxToAdd = findMinMaxEditablePictures(pageDefinitionsToAddNext);
    const nbMinPages = minPages ? minPages : 1;

    minEditablePictures = totalMinMaxInContent.min + minMax.min * (nbMinPages - pagesCount);
    maxEditablePictures = totalMinMaxInContent.max + minMax.max * (nbMaxPages - pagesCount);
    minEmptyEditablePictures =
      Math.max(0, minMaxToAdd.min * (nbMinPages - pagesCount)) + totalMinMaxSlotsInContent.min;
    maxEmptyEditablePictures =
      nbMaxPages > 0
        ? minMaxToAdd.max * (nbMaxPages - pagesCount) + totalMinMaxSlotsInContent.max
        : 0;
  }

  const addMorePhotosEnabled = useMemo(
    () =>
      !pageKey ||
      maxEmptyEditablePictures === 0 ||
      selectedPictures.length < maxEmptyEditablePictures,
    [maxEmptyEditablePictures, pageKey, selectedPictures]
  );

  const addToSelectedPictures = useCallback(
    (photoList: SelectionPhoto[]) => {
      if (!addMorePhotosEnabled) {
        return;
      }
      setSelectedPictures((selectedPicturesValue) => [...selectedPicturesValue, ...photoList]);
    },
    [addMorePhotosEnabled, setSelectedPictures]
  );

  const hasSelectionOutside = hasSelectionInPanel(template);

  const onFileChange = useCallback(
    (files: File[]) => {
      // to deactivate easily if needed
      const isReorderedByDate = true;
      const uploadedFiles: SelectionPhoto[] = [];

      if (!uploadProvider) {
        return Promise.resolve();
      }

      const addPicturesByDate = (selectedFiles: SelectionPhoto[]) => {
        if (isReorderedByDate && selectedFiles.length > 0) {
          let orderedFiles = orderSelectionByDate(selectedFiles);
          if (pageKey) {
            orderedFiles = orderedFiles.slice(
              0,
              maxEmptyEditablePictures - selectedPictures.length
            );
          }
          addToSelectedPictures([...orderedFiles]);
        }
      };

      return Promise.all(
        map(files, (file, index) => {
          return handleFile(file, dispatch, uploadProvider).then((selectionPhoto) => {
            if (!selectionPhoto || selectionPhoto.status === "error") {
              return;
            }
            if (
              content.pages.length === 1 &&
              findPagesWithEmptyEditablePictures(content.pages, template).length === 0 &&
              (!pageKey || maxEmptyEditablePictures === 0)
            ) {
              return;
            }
            if (
              maxPageReached(content, configuration, noCoverPageDefinitions) &&
              findPagesWithEmptyEditablePictures(content.pages, template).length === 0 &&
              (!pageKey || maxEmptyEditablePictures === 0)
            ) {
              return;
            }

            if (pageKey && editedPicture && index === 0) {
              addToSelectedPictures([selectionPhoto]);
              dispatch({
                configuration,
                editablePictureKey: editedPicture.editablePictureKey,
                pageKey,
                selectionPhoto,
                tryToFillFirstEmptyPicture: false,
                type: "addPicture",
                undoKey: `PictureEditorView-${pageKey}`,
              });
            } else if (isReorderedByDate) {
              uploadedFiles.push(selectionPhoto);
            } else {
              addToSelectedPictures([selectionPhoto]);
            }

            if (uploadedFiles.length === files.length && !hasSelectionOutside) {
              if (pageKey && selectedPictures.length < maxEmptyEditablePictures) {
                const limitedFiles = uploadedFiles.slice(
                  0,
                  maxEmptyEditablePictures - selectedPictures.length
                );
                addToSelectedPictures(limitedFiles);
              } else {
                addPicturesByDate(uploadedFiles);
              }
            }

            setUploadingQuantity((prevUploadingQuantity) =>
              prevUploadingQuantity && prevUploadingQuantity > 0
                ? prevUploadingQuantity - 1
                : prevUploadingQuantity
            );
          });
        })
      ).then(() => {
        setUploadingQuantity(undefined);
        return Promise.resolve();
      });
    },
    [
      addToSelectedPictures,
      configuration,
      content,
      dispatch,
      editedPicture,
      hasSelectionOutside,
      maxEmptyEditablePictures,
      noCoverPageDefinitions,
      pageKey,
      selectedPictures,
      setUploadingQuantity,
      template,
      uploadProvider,
    ]
  );

  const handleChangeUploadQuantity = (quantity: number | undefined) => {
    setUploadingQuantity(quantity);
  };

  const closeGallery = () => {
    dispatch({
      open: false,
      type: "setGallery",
    });
    if (!pageKey && !editedPicture) {
      dispatch({ type: "setEventQueue", way: "use" });
    }
    if (
      hasSelectionOutside &&
      (selectionPhotos.length > 0 || selectedPictures.length > 0) &&
      (panel.current === Panel.EDITPAGE || panel.current === Panel.EDITCOVER)
    ) {
      dispatch({
        key: panel.key,
        openedTab: "photos",
        panel: panel.current,
        type: "setPanel",
      });
    }
  };

  const handleCloseGallery = useCallback(() => {
    if (
      !hasSelectionOutside &&
      uploadingQuantity &&
      uploadingQuantity > 1 &&
      agreeEarlyClosing === false
    ) {
      setShowConfirmationModal(true);
      setActionToConfirm("close");
      return;
    }

    if (removedPictures.length > 0 && !editedPicture) {
      dispatch({ type: "undoRedo", way: "undo" });
    }
    closeGallery();
  }, [
    agreeEarlyClosing,
    closeGallery,
    dispatch,
    editedPicture,
    hasSelectionOutside,
    removedPictures,
    setShowConfirmationModal,
    uploadingQuantity,
  ]);

  const alreadySelectedPhotos = useMemo(() => {
    if (editedPicture && (selectedPictures.length > 0 || removedPictures.length > 0)) {
      return [];
    } else if (editedPicture && page) {
      const element = page.editablePictures.find(
        (elem) => elem.editablePictureKey === editedPicture.editablePictureKey
      );
      if (!element) {
        return [];
      }
      const selectedPicture = findPhoto(element.selectionPhotoKey, {
        photos: selectionPhotos,
      });
      return selectedPicture ? [selectedPicture] : [];
    } else if (!hasSelectionOutside || !!page) {
      return findSelectionPhotosInPages(page ? [page] : pagesWithoutCover, selectionPhotos);
    } else {
      return selectionPhotos;
    }
  }, [
    editedPicture,
    hasSelectionOutside,
    page,
    pagesWithoutCover,
    removedPictures,
    selectedPictures,
    selectionPhotos,
  ]);

  const getSelectedPhotoKeys = useCallback(
    (provider) => {
      const alreadySelectedEditablePictures: SelectionPhoto[] = alreadySelectedPhotos;

      return [...alreadySelectedEditablePictures, ...selectedPictures]
        .filter((picture) => picture.provider === provider)
        .map((picture) => {
          return { key: picture.key, providerRef: picture.providerRef };
        });
    },
    [alreadySelectedPhotos, selectedPictures]
  );

  const handleSelectPhoto = useCallback(
    (selectionPhoto: SelectionPhoto) => {
      let alreadySelected = false;
      // if displayed on product --> delete from pages & selection
      // if displayed in selected photos --> delete from selectedPictures
      if (isPhotoInPicturesList(selectionPhoto, alreadySelectedPhotos)) {
        dispatch({
          pageKey,
          selectionPhoto,
          type: "removePictureFromContent",
          undoKey: `gallery-${galleryKey}`,
        });
        setRemovedPictures((removedPicturesValue) => [...removedPicturesValue, selectionPhoto]);
        alreadySelected = true;
      }
      if (isPhotoInPicturesList(selectionPhoto, selectedPictures)) {
        setSelectedPictures((selectedPicturesValue) =>
          selectedPicturesValue.filter(
            (picture) =>
              !(
                picture.key === selectionPhoto.key ||
                picture.providerRef === selectionPhoto.providerRef
              )
          )
        );
      } else if (editedPicture) {
        dispatch({
          configuration,
          editablePictureKey: editedPicture.editablePictureKey,
          pageKey,
          selectionPhoto,
          tryToFillFirstEmptyPicture: false,
          type: "addPicture",
          undoKey: `gallery-${galleryKey}`,
        });
        closeGallery();
      } else if (
        alreadySelected === false &&
        (maxPages === undefined ||
          selectedPictures.length < maxEmptyEditablePictures ||
          hasSelectionOutside)
      ) {
        addToSelectedPictures([selectionPhoto]);
      }
    },
    [
      addToSelectedPictures,
      alreadySelectedPhotos,
      configuration,
      dispatch,
      editedPicture,
      hasSelectionOutside,
      galleryKey,
      closeGallery,
      maxEditablePictures,
      maxEmptyEditablePictures,
      maxPages,
      pageKey,
      removedPictures,
      selectedPictures,
      selectionPhotos,
      setSelectedPictures,
    ]
  );

  let instructionMessage = intl.formatMessage({
    id: "kustom_SelectionModal_title",
  });
  if (template.templateType === "print") {
    instructionMessage = intl.formatMessage({
      id: "kustom_SelectionModal_select__print",
    });
  } else if (maxEditablePictures > 0 && minEditablePictures !== maxEditablePictures) {
    instructionMessage = intl.formatMessage(
      { id: "kustom_SelectionModal_selectBetween" },
      {
        countMax: maxEditablePictures,
        countMin: minEditablePictures || 1,
      }
    );
  } else if (minEditablePictures > 0 || maxEditablePictures > 0) {
    instructionMessage = intl.formatMessage(
      { id: "kustom_SelectionModal_selectphotos" },
      { count: minEditablePictures || maxEditablePictures }
    );
  }

  const handleValidateGallery = useCallback(() => {
    if (
      !hasSelectionOutside &&
      uploadingQuantity &&
      uploadingQuantity > 1 &&
      agreeEarlyClosing === false
    ) {
      setShowConfirmationModal(true);
      setActionToConfirm("valid");
      return;
    }

    if (
      minEmptyEditablePictures === 0 &&
      nbMaxPages > 0 &&
      maxEmptyEditablePictures === 0 &&
      removedPictures.length === 0 &&
      !hasSelectionOutside
    ) {
      closeGallery();
      return;
    }

    if (
      pageKey &&
      fixedLayout === false &&
      page &&
      currentEmptyEditablePictures < selectedPictures.length
    ) {
      const newPageDefinition = getNextLayoutWithMorePictures(
        page.pageDefinitionKey,
        conditionFilteredPageDefinitions,
        selectedPictures.length - currentEmptyEditablePictures
      );
      if (newPageDefinition) {
        dispatch({
          options: configuration?.options,
          pageDefinitionKey: newPageDefinition.key,
          pageKey,
          type: "changePagePageDefinition",
          undoKey: `gallery-${galleryKey}`,
        });
      }
    }

    if (editedPicture) {
      closeGallery();
    } else if (pageKey) {
      for (let i = 0; i < selectedPictures.length && i < maxEmptyEditablePictures; i++) {
        const selectionPhoto = selectedPictures[i];
        dispatch({
          configuration,
          pageKey,
          selectionPhoto,
          tryToFillFirstEmptyPicture: true,
          type: "addPicture",
          undoKey: `gallery-${galleryKey}`,
        });
      }
    } else if (hasSelectionOutside) {
      for (const selectionPhoto of selectedPictures) {
        dispatch({
          selectionPhoto,
          type: "addOrUpdateSelectionPhoto",
        });
      }
    } else {
      // if global, go autofill:
      dispatch({
        configuration,
        selectedPictures,
        type: "addPicturesWithAutofill",
        undoKey: `gallery-${galleryKey}`,
      });
    }

    closeGallery();
  }, [
    agreeEarlyClosing,
    configuration,
    currentEmptyEditablePictures,
    dispatch,
    editedPicture,
    fixedLayout,
    galleryKey,
    closeGallery,
    conditionFilteredPageDefinitions,
    maxEmptyEditablePictures,
    minEmptyEditablePictures,
    page,
    pageKey,
    removedPictures.length,
    selectedPictures,
    setActionToConfirm,
    setShowConfirmationModal,
    uploadingQuantity,
  ]);

  const handleDismissModal = useCallback(() => {
    setAgreeEarlyClosing(true);
    setShowConfirmationModal(false);
  }, [setAgreeEarlyClosing, setShowConfirmationModal]);

  const handleConfirmModal = useCallback(() => {
    handleDismissModal();

    if (actionToConfirm === "close") {
      handleCloseGallery();
    } else if (actionToConfirm === "valid") {
      handleValidateGallery();
    }
  }, [actionToConfirm, handleCloseGallery, handleDismissModal, handleValidateGallery]);

  const selectedCount = selectedPictures.length + alreadySelectedPhotos.length;
  const canSelectMax = maxEditablePictures ? maxEditablePictures - selectedCount : undefined;

  return (
    <div className="SelectionModal">
      <div className="SelectionModal-overlay" onClick={handleCloseGallery} />

      <div className="SelectionModal-main">
        <div className="SelectionModal-header">
          <div className="SelectionModal-header-left">
            <button className="SelectionModal-header-quit" onClick={handleCloseGallery}>
              <IconX height="16" width="16" />
              <span className="SelectionModal-header-quit-text">
                <FormattedMessage id="kustom_quit" />
              </span>
            </button>
          </div>
          <div className="SelectionModal-header-title">{instructionMessage}</div>
          <div className="SelectionModal-header-right">
            <button
              className="SelectionModal-header-valid"
              disabled={
                hasSelectionOutside === false &&
                ((selectedPictures.length === 0 && removedPictures.length === 0) ||
                  (selectedPictures.length === 0 &&
                    removedPictures.length > 0 &&
                    alreadySelectedPhotos.length === 0))
              }
              onClick={handleValidateGallery}
            >
              <FormattedMessage id="kustom_EditableBookTitle_validate" />
              <span className="SelectionModal-header-valid-count">{selectedCount}</span>
            </button>
          </div>
        </div>

        <div className="SelectionModal-tabs">
          {getVariableByEnv("NODE_ENV") === "development" ? (
            <SelectionHeaderProvider
              onClick={handleChangeCurrentProvider}
              provider="unsplash"
              selected={currentProvider === "unsplash"}
            />
          ) : null}
          <SelectionHeaderProvider
            onClick={handleChangeCurrentProvider}
            provider="upload"
            selected={currentProvider === "upload"}
          />
          <SelectionHeaderProvider
            onClick={handleChangeCurrentProvider}
            provider="friends_sharing"
            selected={currentProvider === "friends_sharing"}
          />
          <SelectionHeaderProvider
            onClick={handleChangeCurrentProvider}
            provider="google_photos"
            selected={currentProvider === "google_photos"}
          />
          <SelectionHeaderProvider
            onClick={handleChangeCurrentProvider}
            provider="instagram"
            selected={currentProvider === "instagram"}
          />
          <SelectionHeaderProvider
            onClick={handleChangeCurrentProvider}
            provider="facebook"
            selected={currentProvider === "facebook"}
          />
        </div>

        <div className="SelectionModal-content">
          {currentProvider === "facebook" &&
          facebookSelectionProvider.isBlank() &&
          selectionProviderConnect ? (
            <div className="SelectionModal-content-add">
              <div className="SelectionModal-content-add-button">
                <Button onClick={() => selectionProviderConnect("facebook")}>
                  <IconFacebook />
                  <FormattedMessage id="kustom_Selection_footer_buttonLabel_facebook" />
                </Button>
              </div>
              <div className="SelectionModal-content-add-intro">
                <FormattedMessage id="kustom_Selection_footer_intro_facebook" />
              </div>
            </div>
          ) : null}
          {currentProvider === "google_photos" &&
          googleSelectionProvider.isBlank() &&
          selectionProviderConnect ? (
            <div className="SelectionModal-content-add SelectionModal-content-add-google">
              <div className="SelectionModal-content-add-intro">
                <FormattedMessage id="kustom_Selection_footer_intro_google" />
              </div>
              <div className="SelectionModal-content-add-description">
                <FormattedMessage id="kustom_Selection_footer_description_google" />
              </div>
              <button
                className="SelectionModal-content-add-button-google"
                onClick={() => selectionProviderConnect("google_photos")}
              >
                <GoogleIcon />
                <FormattedMessage id="kustom_Selection_footer_buttonLabel_google" />
              </button>
            </div>
          ) : null}
          {currentProvider === "instagram" &&
          instagramSelectionProvider.isBlank() &&
          selectionProviderConnect ? (
            <div className="SelectionModal-content-add">
              <div className="SelectionModal-content-add-button">
                <Button onClick={() => selectionProviderConnect("instagram")}>
                  <IconInstagram />
                  <FormattedMessage id="kustom_Selection_footer_buttonLabel_instagram" />
                </Button>
              </div>
              <div className="SelectionModal-content-add-intro">
                <FormattedMessage id="kustom_Selection_footer_intro_instagram" />
              </div>
            </div>
          ) : null}
          {currentProvider === "friends_sharing" ? (
            <div className="SelectionModal-content-add">
              <IconPictures />
              <div className="SelectionModal-content-add-title">
                <FormattedMessage id="kustom_Selection_footer_intro_friendsSharing" />
              </div>
              <div className="SelectionModal-content-add-description">
                <FormattedMessage
                  id="kustom_Selection_footer_description_friendsSharing"
                  values={{
                    strong: (...chunks: string[]) => <strong>{chunks}</strong>,
                  }}
                />
              </div>
              <div className="SelectionModal-content-add-description SelectionModal-content-add-description-warning">
                <FormattedMessage id="kustom_Selection_footer_warning_friendsSharing" />
              </div>
            </div>
          ) : null}

          <div
            className={classNames(
              "SelectionModal-content-main",
              `SelectionModal-content-main-${currentProvider}`,
              {
                "SelectionModal-content-main--selectionDisabled": !addMorePhotosEnabled,
              }
            )}
          >
            {currentProvider === "facebook" && facebookSelectionProvider ? (
              <SelectionProviderBrowser
                onSelect={handleSelectPhoto}
                pageKey={pageKey}
                provider="facebook"
                providerInstance={facebookSelectionProvider}
                selectedPhotoKeys={getSelectedPhotoKeys("facebook")}
                selectionPhotos={selectionPhotos}
                uploadProvider={uploadProvider}
              />
            ) : null}
            {currentProvider === "google_photos" && googleSelectionProvider ? (
              <SelectionProviderBrowser
                onSelect={handleSelectPhoto}
                pageKey={pageKey}
                provider="google_photos"
                providerInstance={googleSelectionProvider}
                selectedPhotoKeys={getSelectedPhotoKeys("google_photos")}
                selectionPhotos={selectionPhotos}
                selectionProviderConnect={selectionProviderConnect}
                selectionProviderDisconnect={selectionProviderDisconnect}
                uploadProvider={uploadProvider}
              />
            ) : null}
            {currentProvider === "instagram" && instagramSelectionProvider ? (
              <SelectionProviderBrowser
                onSelect={handleSelectPhoto}
                pageKey={pageKey}
                provider="instagram"
                providerInstance={instagramSelectionProvider}
                selectedPhotoKeys={getSelectedPhotoKeys("instagram")}
                selectionPhotos={selectionPhotos}
                uploadProvider={uploadProvider}
              />
            ) : null}
            {currentProvider === "unsplash" && unsplashSelectionProvider ? (
              <SelectionProviderBrowser
                onSelect={handleSelectPhoto}
                pageKey={pageKey}
                provider="unsplash"
                providerInstance={unsplashSelectionProvider}
                selectedPhotoKeys={getSelectedPhotoKeys("unsplash")}
                selectionPhotos={selectionPhotos}
                uploadProvider={uploadProvider}
              />
            ) : null}
            {currentProvider === "upload" && (
              <UploadManager
                maxSelectableItems={canSelectMax}
                onFileChange={onFileChange}
                onSelectPhoto={handleSelectPhoto}
                onUploadQuantityChange={handleChangeUploadQuantity}
                pageKey={pageKey}
                providerInstance={uploadSelectionProvider}
                selectedPhotoKeys={getSelectedPhotoKeys("upload")}
                selectionPhotos={selectionPhotos}
                uploadProvider={uploadProvider}
              />
            )}
          </div>
        </div>

        <div className="SelectionModal-footer">
          {uploadProvider ? (
            <div className="SelectionModal-uploadSummary">
              <SelectionUploadSummary
                forcedDisplay={!!uploadingQuantity && uploadingQuantity > 0}
                uploadProvider={uploadProvider}
              />
            </div>
          ) : null}
        </div>
        <ConfirmModal
          confirmLabel={intl.formatMessage({
            id: "kustom_SelectionModalquitConfirm_yes",
          })}
          dismissLabel={intl.formatMessage({
            id: "kustom_SelectionModalquitConfirm_no",
          })}
          display={showConfirmationModal}
          onConfirm={handleConfirmModal}
          onDismiss={handleDismissModal}
          validationButtonType="warning"
        >
          <div className="SelectionModal-quitConfirm">
            {intl.formatMessage({
              id: "kustom_SelectionModalquitConfirm_text",
            })}
          </div>
        </ConfirmModal>
      </div>
    </div>
  );
};

export default SelectionModal;
